import React, { useMemo } from 'react';

import { ChartConfig, ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { ResponsiveContainer, BarChart, Bar, Rectangle, CartesianGrid, XAxis } from 'recharts';
import { TooltipProps } from 'recharts';

import { Skeleton } from '@/components/ui/skeleton';

const chartConfig = {
	desktop: {
		label: "Desktop",
		color: '#C0E6FF'
	},
} satisfies ChartConfig;

interface EventTypeDistributionChartProps {
	data: Array<any>;
	isLoading?: boolean;
}

interface CustomTooltipProps extends TooltipProps<number, string> { }

const CustomTooltip: React.FC<CustomTooltipProps> = ({
	active,
	payload,
	label,
}) => {
	if (!active || !payload || payload.length === 0) {
		return null;
	}

	const rawValue = Math.pow(10, (payload as any)[0].value).toFixed(0);

	return (
		<div className="p-2 bg-[#011829]/80 backdrop-blur-md text-white rounded shadow">
			<p className="mb-1 text-sm font-medium">{payload[0].payload['EVENT_TYPE']}</p>
			<p className="text-sm">
				Total Count: {Number(rawValue).toLocaleString()}
			</p>
		</div>
	);
};

export const EventTypeDistributionChart = (props: EventTypeDistributionChartProps) => {
	const { data, isLoading = false } = props;

	const transformedData = useMemo(() => {
		if (!data) return [];


		return data
			.filter((d) => d['TOTAL_COUNT'] > 0)
			.map(d => ({
				...d,
				logValue: Math.log10(d['TOTAL_COUNT'])
			}));
	}, [data]);


	return (
		<>
			{isLoading ?
				<Skeleton className='h-45 w-auto bg-white/20' />
				:
				!!transformedData?.length && (
					<ChartContainer config={chartConfig} className='h-full w-full'>
						<ResponsiveContainer width='100%' height='100%'>
							<BarChart
								data={transformedData}
								margin={{ top: 12, bottom: 0 }}
							>
								<defs>
									<linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor="#A084DC" stopOpacity={0.8} />
										<stop offset="95%" stopColor="#A084DC" stopOpacity={0.1} />
									</linearGradient>
								</defs>
								<CartesianGrid stroke="#000000" strokeDasharray="3 3" vertical={false} />
								<XAxis
									dataKey='TOTAL_COUNT'
									tickLine={false}
									axisLine={false}
									tick={false}
									padding={{ left: 20, right: 20 }}
								/>

								<ChartTooltip
									cursor={{ stroke: '#FFFFFF6F', strokeWidth: 2, strokeDasharray: '1 4' }}
									content={<CustomTooltip />}
								/>

								<Bar
									dataKey="logValue"
									fill="url(#gradient)"
									stroke="#000000"
									strokeWidth={1.5}
									shape={<Rectangle radius={[0, 0, 0, 0]} />}
								/>
							</BarChart>
						</ResponsiveContainer>
					</ChartContainer>
				)
			}
		</>
	);
}